import React, { useMemo } from "react";
import { GitMerge, Spline } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import MutedText from "../ui/MutedText";
import { useBodies, useEqualities, useJoints, useSites, useTendons } from "../../stores/sceneStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { getConstraintDisplayName } from "../../utils/constraintNames";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";
import InspectableMenuRow from "./InspectableMenuRow";

const targetTypeLabel = (type) => {
  if (type === "body") return "Body";
  if (type === "site") return "Site";
  if (type === "joint") return "Joint";
  if (type === "tendon") return "Tendon";
  return "Target";
};

/** Pure helper shared with inspectors that need spacing logic without mounting the section. */
export function computeRelatedConstraintsLists({
  bodyUuid = null,
  jointUuid = null,
  bodies,
  tendons,
  equalities,
}) {
  const targetIds = new Set();
  const jointIds = new Set();

  if (bodyUuid) {
    const body = bodies?.[bodyUuid];
    targetIds.add(bodyUuid);
    (body?.sites || []).forEach((siteId) => {
      if (siteId) targetIds.add(siteId);
    });
    (body?.joints || []).forEach((bodyJointUuid) => {
      if (!bodyJointUuid) return;
      targetIds.add(bodyJointUuid);
      jointIds.add(bodyJointUuid);
    });
  }

  if (jointUuid) {
    targetIds.add(jointUuid);
    jointIds.add(jointUuid);
  }

  const tendonRows = Object.values(tendons || {}).filter((tendon) =>
    (tendon?.joints || []).some((entry) => entry?.joint && jointIds.has(entry.joint))
  );

  tendonRows.forEach((tendon) => {
    if (tendon?.uuid) targetIds.add(tendon.uuid);
    if (tendon?.name) targetIds.add(tendon.name);
  });

  const equalityRows = Object.values(equalities || {}).filter((equality) => {
    if (!equality) return false;
    return (
      (equality.obj1 && targetIds.has(equality.obj1)) ||
      (equality.obj2 && targetIds.has(equality.obj2))
    );
  });

  return {
    relatedEqualities: equalityRows.sort((a, b) =>
      getConstraintDisplayName(a, equalities).localeCompare(getConstraintDisplayName(b, equalities))
    ),
    relatedTendons: tendonRows.sort((a, b) => (a?.name || "").localeCompare(b?.name || "")),
  };
}

const RelatedConstraintsSection = ({
  bodyUuid = null,
  jointUuid = null,
  defaultTendonsOpen = false,
}) => {
  const bodies = useBodies();
  const joints = useJoints();
  const sites = useSites();
  const tendons = useTendons();
  const equalities = useEqualities();
  const selectEquality = useSelectionStore((state) => state.selectEquality);
  const selectTendon = useSelectionStore((state) => state.selectTendon);
  const setLastSelectionSource = useSelectionStore((state) => state.setLastSelectionSource);

  const { relatedEqualities, relatedTendons } = useMemo(
    () =>
      computeRelatedConstraintsLists({
        bodyUuid,
        jointUuid,
        bodies,
        tendons,
        equalities,
      }),
    [bodyUuid, jointUuid, bodies, tendons, equalities]
  );

  if (relatedEqualities.length === 0 && relatedTendons.length === 0) return null;

  const describeTarget = (targetUuid, objtype) => {
    if (!targetUuid) return "?";
    if (objtype === "body") return stripPrefix(bodies[targetUuid]?.name) || targetUuid;
    if (objtype === "joint") return stripPrefix(joints[targetUuid]?.name) || targetUuid;
    if (objtype === "site") return stripPrefix(sites[targetUuid]?.name) || targetUuid;
    if (objtype === "tendon") return stripPrefix(tendons[targetUuid]?.name) || targetUuid;
    return targetUuid;
  };

  const focusEquality = (uuid) => {
    selectEquality(uuid);
    setLastSelectionSource("sceneTree");
  };

  const focusTendon = (uuid) => {
    selectTendon(uuid);
    setLastSelectionSource("sceneTree");
  };

  return (
    <>
      {relatedEqualities.length > 0 ? (
        <Collapsible defaultOpen={false}>
          <CollapsibleTrigger quiet>Constraints ({relatedEqualities.length})</CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-1">
              {relatedEqualities.map((equality) => {
                const displayName = getConstraintDisplayName(equality, equalities);
                return (
                  <InspectableMenuRow
                    key={equality.uuid}
                    onInspect={() => focusEquality(equality.uuid)}
                    inspectLabel={`Inspect ${displayName}`}
                    rightContent={
                      <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400">
                        <GitMerge className="w-3 h-3" />
                        {equality.type}
                      </span>
                    }
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{displayName}</span>
                      <MutedText as="span" className="truncate text-xs">
                        {`${targetTypeLabel(equality.objtype)}: ${describeTarget(equality.obj1, equality.objtype)} -> ${describeTarget(equality.obj2, equality.objtype)}`}
                      </MutedText>
                    </span>
                  </InspectableMenuRow>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ) : null}

      {relatedTendons.length > 0 ? (
        <Collapsible defaultOpen={defaultTendonsOpen}>
          <CollapsibleTrigger quiet>Tendons ({relatedTendons.length})</CollapsibleTrigger>
          <CollapsibleContent>
            <div className="space-y-1">
              {relatedTendons.map((tendon) => {
                const displayName = stripPrefix(tendon.name) || "Tendon";
                return (
                  <InspectableMenuRow
                    key={tendon.uuid}
                    onInspect={() => focusTendon(tendon.uuid)}
                    inspectLabel={`Inspect ${displayName}`}
                    rightContent={
                      <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400">
                        <Spline className="w-3 h-3" />
                        {tendon.joints?.length || 0}
                      </span>
                    }
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{displayName}</span>
                      <MutedText as="span" className="truncate text-xs">
                        {(tendon.joints || [])
                          .map((entry) => stripPrefix(joints[entry?.joint]?.name) || entry?.joint)
                          .filter(Boolean)
                          .join(", ")}
                      </MutedText>
                    </span>
                  </InspectableMenuRow>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </>
  );
};

export default React.memo(RelatedConstraintsSection);
